import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { ObjectLabel, Sideloadable } from 'app/providers/storage-api/object-label.service';
import { StorageAPIService, StorageListResult } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { CollectionService } from './collection.service';
import { SectionKeyMoment } from './document.service';
import { urlFor } from './utils';

export interface Analysis {
  name: string;
  analysisName?: string;
  displayName: string;
  collectionName: string;
  createdAt: Date;
  modifiedAt: Date;
  configId: string;
  notes: string;
  query: string;
  status: string;
  statusText: string | null;
  progress: number;
  analysisCount: number;
  documentCount: number;
  inflightCount: number;
  processedCount: number;
  sentCount: number;
  minDocAt: Date;
  maxDocAt: Date;
  hasTaxonomy: boolean;
  hasDateData: boolean;
  entityTypes: string[];
  topicTypes: string[];
  customFields: string[];
  standardFields: string[];
  hasEntities: boolean;
  hasPhrases: boolean;
  hasThemes: boolean;
  hasTopics: boolean;
  hasTopicMentions: boolean;
  hasTopicPhrases: boolean;
  hasTopicThemes: boolean;
  hasThemeMentions: boolean;
  hasEntityMentions: boolean;
  hasEntityPhrases: boolean;
  sourceVersion: number;
  configName: string;
  clauseModelTypes: string[];
  lastRunAt: Date;
  isReAnalyze: boolean;
  embeddingStatus?: string;
  embeddingModel?: string;
  embeddingStrategy?: string;
  embeddingDocumentCount?: number;
  isSocial?: boolean;
  unAliasedCustomFields?: string[];
  unAliasedWeightFields?: any[];
  primary?: boolean;
  objectLabels?: string[] | ObjectLabel[];
  hasSectionSentiment?: boolean;
  sectionPropertyTypes?: string[];
}

export interface AnalysisCreateOptions {
  projectId: string;
  collectionName: string;
  analysisName: string;
  configId: string;
  notes: string;
  query: string;
}

export interface AnalysisUpdateOptions {
  projectId: string;
  collectionName: string;
  analysisId: string;
  displayName?: string;
  notes?: string;
  makePrimary?: boolean;
}

export interface DocumentAnalysis {
  id?: string;
  analysisName: string;
  analysisType: string;
  annotator: string;
  configId: string;
  analysisConfigId?: string;
  details?: any;
  displayName: string;
  documentId: string;
  entities: any[];
  language?: string;
  languageScore: number;
  phrases: any[];
  projectId: string;
  relations?: any;
  sentimentPolarity: string;
  sentimentPolarity3: string;
  sentimentPolarity5: string;
  sentimentScore: number;
  summary: string;
  themes: any[];
  topics: any[];
  taxonomy: any[];
  translatedText?: string;
  redactedText?: string;
  redactedSummary?: string;
  flatSections?: Section[];
}

export interface Section extends SectionProperties {
  sectionId: number;
  parentId?: number;
  name: string;
  sentimentScore?: number;
  aliases: string[];
  value: string;
  redactedValue?: string;
  originalValue?: string;
  processAsText: boolean;
  charOffset: number;
  charLength: number;
  metadata?: {
    [key: string]: any;
  };
  clauses?: DocumentClause[];
  keyMoments?: SectionKeyMoment[];
}

export interface SectionProperties {
  segment?: string[];
  sectionType?: 'utterance' | 'summary' | 'other';
  speakerName?: string;
  speakerType?: string;
  sectionLabel?: string;
  sectionTime?: string;
  sectionPropertiesId?: string;
}

export interface DocumentClause {
  id?: string;
  type: string;
  title?: string;
  sentiment_score?: number;
  section?: Section;
  purpose?: string[];
  mentions?: {
    isNegated?: boolean;
    label?: string;
    sentiment_score?: number; // currently used in clause-level sentiment
    locations?: {
      char_length: number;
      char_offset: number;
      length: number;
      offset: number;
      section: number;
      section_char_offset: number;
    }[];
  }[];
}

export interface DocumentCountLookup {
  documentCounts: { ownerType: 'ANALYSIS' | 'COLLECTION'; value: string; count: number }[];
}

@EnableClassTelemetry()
@Injectable()
export class AnalysisService extends StorageAPIService {
  static map(item: Analysis, labels?: ObjectLabel[]): Analysis {
    item.createdAt = new Date(item.createdAt);
    item.modifiedAt = new Date(item.modifiedAt);
    item.minDocAt = item.minDocAt != null ? new Date(item.minDocAt) : null;
    item.maxDocAt = item.maxDocAt != null ? new Date(item.maxDocAt) : null;
    item.hasDateData = item.maxDocAt != null && item.minDocAt != null;
    item.objectLabels = this.mapObjectLabels(item, labels);

    return item;
  }

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private realtimeService: RealtimeService,
    private collectionService: CollectionService,
    private accountService: StorageAccountService
  ) {
    super(http, systemService);
  }

  on(namespace: string, fn: RealtimeEventHandler<Analysis>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        analysis: (data: RealtimeMessage<Analysis>) => {
          AnalysisService.map(data.object);
          fn(data);
        },
      },
      'analysisService'
    );
  }

  createAnalysis(options: Partial<AnalysisCreateOptions>): Promise<void> {
    const opts = Object.assign(
      {
        notes: '',
        query: '*:*',
      },
      options
    );
    return this.post<void>(urlFor(`/collections/${opts.collectionName}/analyses`, opts.projectId), {
      name: opts.analysisName,
      configId: opts.configId,
      notes: opts.notes,
      query: opts.query,
    });
  }

  updateAnalysis(options: AnalysisUpdateOptions): Promise<Analysis> {
    const params: Partial<{ notes: string; displayName: string; makePrimary: boolean }> = {};

    if (typeof options.notes !== 'undefined') {
      params.notes = options.notes;
    }
    if (typeof options.displayName !== 'undefined') {
      params.displayName = options.displayName;
    }
    if (typeof options.makePrimary !== 'undefined') {
      params.makePrimary = options.makePrimary;
    }
    return this.put<Analysis>(
      urlFor(
        `/collections/${options.collectionName}/analyses/${options.analysisId}`,
        options.projectId
      ),
      params
    );
  }

  async getAllAnalyses(projectId?: string): Promise<StorageListResult<Analysis>> {
    const result = await this.getPage<Analysis>(urlFor('/analyses', projectId), {
      'page-limit': StorageAPIService.MAX_LIMIT,
      status: '*',
    });
    result.items = result.items.map((a) => AnalysisService.map(a));
    return result;
  }

  // TODO: Need real API
  async getAllSocialAnalyses(projectId?: string): Promise<Analysis[]> {
    const socialMetaDatas = ['Likes', 'Dislikes', 'Comments', 'Shares'];

    const accountMetadata = await this.accountService.groupFieldsSub.latest();
    let scMdIds = [];
    if (accountMetadata) {
      scMdIds = accountMetadata.metadata
        .filter((md) => socialMetaDatas.includes(md.label))
        .map((md) => md.fieldName);
    }

    const collections = await this.collectionService.getSocialCollections(projectId);
    const mockSocialAnalyses = collections.items.map((item) => {
      return {
        progress: 0.985,
        name: item['id'] + '_analysis',
        query: '*:*',
        displayName: item.displayName,
        minDocAt: item.indexedAt,
        maxDocAt: item.driverAnalysisAt,
        sourceVersion: 7,
        statusText: '',
        requeueCount: 0,
        customFields: scMdIds,
        status: 'IDLE',
        configName: 'English',
        clauseModelTypes: null,
        documentCount: item.documentCount,
        modifiedAt: new Date('2024-04-23T08:35:26.000Z'),
        standardFields: ['language', 'creator', 'url', 'source'],
        processedCount: 97,
        analysisCount: item.analysisCount,
        inflightCount: 0,
        sentCount: 100,
        entityTypes: [
          'Company',
          'Currency amount',
          'Date',
          'Hashtag',
          'Organization',
          'Person',
          'Phone',
          'Place',
          'Product',
          'Publication',
          'Quote',
          'Url',
        ],
        topicTypes: [],
        configId: 'lt:en',
        collectionName: item.collectionName,
        externalId: null,
        createdAt: new Date('2024-04-23T08:34:56.000Z'),
        notes: '',
        lastRequeuedAt: null,
        lastRunAt: new Date('2024-04-23T10:35:02+0200'),
        hasTaxonomy: true,
        hasThemes: true,
        hasTopics: true,
        hasTopicPhrases: true,
        hasTopicThemes: false,
        hasEntities: true,
        hasPhrases: true,
        isReAnalyze: false,
        hasEntityPhrases: true,
        hasThemeMentions: true,
        hasTopicMentions: false,
        hasEntityMentions: true,
        hasDateData: true,
        isSocial: true,
      } as Analysis;
    });
    return new Promise((resolve) => resolve(mockSocialAnalyses));
  }

  async getAnalysesFor(
    collectionName: string,
    projectId?: string,
    getAll = false
  ): Promise<StorageListResult<Analysis>> {
    const result = await this.getPage<Analysis>(
      urlFor(`/collections/${collectionName}/analyses`, projectId),
      {
        'page-limit': StorageAPIService.MAX_LIMIT,
        status: '*',
        'get-all-collections': getAll.toString(),
      }
    );

    result.items = result.items.map((a) => AnalysisService.map(a));

    return result;
  }

  async getAnalysisByName(
    collectionName: string,
    analysisName: string, // this is uuid in case of projects
    projectId?: string,
    sideloads?: 'objectLabels'
  ): Promise<Analysis> {
    const params = sideloads ? { sideloads } : {};

    const result = await this.get<Sideloadable<Analysis>>(
      urlFor(`/collections/${collectionName}/analyses/${analysisName}`, projectId),
      params
    );

    return AnalysisService.map(result, result.sideloaded?.objectLabels);
  }

  requeueAnalysis(analysis: Analysis): Promise<Analysis> {
    const url = `/analyses/${analysis.name}/_status?status=REQUEUE`;
    return this.post<Analysis>(url, {});
  }

  deleteAnalysis(collectionName: string, analysisName: string, projectId?: string): Promise<void> {
    return this.delete<void>(
      urlFor(`/collections/${collectionName}/analyses/${analysisName}`, projectId)
    );
  }

  reAnalyzeAnalysis(
    collectionName: string,
    analysisName: string,
    projectId?: string,
    configId?: string
  ): Promise<void> {
    return this.post<void>(
      urlFor(
        `/collections/${collectionName}/analyses/${analysisName}/_reanalyze?configId=${configId}`,
        projectId
      )
    );
  }

  stopReAnalyzeAnalysis(
    collectionName: string,
    analysisName: string,
    projectId?: string
  ): Promise<void> {
    return this.post<void>(
      urlFor(`/collections/${collectionName}/analyses/${analysisName}/_reanalyze_stop`, projectId)
    );
  }

  getAnalysisDocumentCount(projectId: string, lookupId: string): Promise<DocumentCountLookup> {
    const query = `publicId:_lookup_/${lookupId}`;
    return this.get(urlFor(`/projects/${projectId}/analyses/_counts`), { query });
  }
}
